import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  FiSearch, FiClock, FiRefreshCw, FiMessageSquare,
  FiThumbsUp, FiThumbsDown, FiX,
} from 'react-icons/fi';
import { useToast } from '../../context/ToastContext';
import '../../styles/QuoteProposal.css';
import '../commissionscan/CommissionScan.css';
import PreviewIcon from '../../assets/images/ViewDetailsIcon.svg';
const PAGE_SIZE = 10;
const getToken  = () => localStorage.getItem('toolboxToken') || '';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  Success: { label: 'Success', color: '#34d399' },
  Failed: { label: 'Failed', color: '#f87171' },
};

const formatDate = (d: string): string => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${m[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
  } catch { return d; }
};

interface NaicsHistoryRow {
  id: string;
  insuredName: string;
  insuredDatabaseId?: string;
  searchText: string;
  created: string;
  userName: string;
  status: string;
  response: string;
  naicsIndex: number;
  updatedInAMS: boolean;
  isLiked: boolean | null;
  totalCount: number;
}

interface NaicsPreviewItem {
  NaicsCode: string;
  Description: string;
  Success: boolean;
  ErrorMessege: string;
  SearchedText: string;
  updatedInAms: boolean;
}

const NaicsClassificationHistory: React.FC = () => {
  const [rows, setRows]               = useState<NaicsHistoryRow[]>([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [search, setSearch]           = useState('');
  const [page, setPage]               = useState(1);
  const [totalCount, setTotalCount]   = useState(0);

  /* Preview modal */
  const [previewData, setPreviewData] = useState<NaicsPreviewItem[] | null>(null);

  /* Feedback popup */
  const [feedbackRowId,      setFeedbackRowId]      = useState<string | null>(null);
  const [feedbackText,       setFeedbackText]        = useState('');
  const [feedbackRating,     setFeedbackRating]      = useState<boolean | null>(null);
  const [feedbackAnchor,     setFeedbackAnchor]      = useState<{ top: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting]  = useState(false);
  const [feedBackError,      setFeedBackError]       = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);

  const { showSuccess, showError } = useToast();

  /* ── Fetch ── */
  const fetchData = useCallback(async (pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/naics/getexistingdetails?pageNumber=${pageNum}&recordCount=${PAGE_SIZE}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const data: NaicsHistoryRow[] = Array.isArray(res.data) ? res.data : [];
      setRows(data);
      setTotalCount(data.length > 0 ? (data[0].totalCount ?? data.length) : 0);
    } catch (e: any) {
      setError(e.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(page); }, [fetchData, page]);
  useEffect(() => { setPage(1); }, [search]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      r.insuredName?.toLowerCase().includes(q) ||
      r.searchText?.toLowerCase().includes(q) ||
      r.userName?.toLowerCase().includes(q) ||
      r.status?.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageList = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce<(number | '…')[]>((acc, p, idx, arr) => {
      if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  /* ── Preview ── */
  const onPreviewClick = useCallback((row: NaicsHistoryRow) => {
    const fallback: NaicsPreviewItem[] = [{
      NaicsCode: '', Description: '', Success: false,
      ErrorMessege: 'Failed', SearchedText: row.searchText, updatedInAms: false,
    }];
    if (!row.response) { setPreviewData(fallback); return; }
    try {
      const parsed = JSON.parse(row.response);
      const items: NaicsPreviewItem[] = (parsed.CodeInfo || []).map((c: any, idx: number) => ({
        NaicsCode:    c.NaicsCode    || c.naicsCode    || '',
        Description:  c.Description  || c.description  || '',
        Success:      parsed.Success ?? true,
        ErrorMessege: parsed.ErrorMessege || '',
        SearchedText: row.searchText,
        updatedInAms: row.naicsIndex === idx && row.updatedInAMS === true,
      }));
      setPreviewData(items.length > 0 ? items : fallback);
    } catch {
      setPreviewData(fallback);
    }
  }, []);


  /* ── Feedback ── */
  const openFeedback = useCallback((row: NaicsHistoryRow, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackRowId(row.id);
    setFeedbackText('');
    setFeedbackRating(row.isLiked ?? null);
    setFeedBackError('');
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackRowId(null);
    setFeedbackAnchor(null);
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedBackError('');
  }, []);

  const saveRating = useCallback((id: string, isLiked: boolean | null) => {
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/naics/naicsratings`,
      { Id: id, IsLiked: isLiked },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    ).then(() => showSuccess('Feedback submitted successfully!')).catch(() => { /* silent */ });
  }, []);

  const onThumbsUpClick = useCallback(() => {
    if (!feedbackRowId) return;
    const cur = rows.find(r => r.id === feedbackRowId);
    const next: boolean | null = cur?.isLiked === true ? null : true;
    setFeedbackRating(next);
    setRows(prev => prev.map(r => r.id === feedbackRowId ? { ...r, isLiked: next } : r));
    saveRating(feedbackRowId, next);
    closeFeedback();
  }, [feedbackRowId, rows, saveRating, closeFeedback]);

  const onThumbsDownClick = useCallback(() => {
    if (!feedbackRowId) return;
    const cur = rows.find(r => r.id === feedbackRowId);
    const next: boolean | null = cur?.isLiked === false ? null : false;
    setFeedbackRating(next);
    setRows(prev => prev.map(r => r.id === feedbackRowId ? { ...r, isLiked: next } : r));
    saveRating(feedbackRowId, next);
    closeFeedback();
  }, [feedbackRowId, rows, saveRating, closeFeedback]);

  const onSubmitFeedback = useCallback(() => {
    if (!feedbackRowId) return;
    if (!feedbackText.trim()) { setFeedBackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedBackError('Please provide your feedback within 250 characters'); return; }
    setFeedBackError('');
    setFeedbackSubmitting(true);
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/naics/naicsfeedback`,
      { Id: feedbackRowId, Feedback: feedbackText },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    )
      .then(() => { setFeedbackSubmitting(false); showSuccess('Feedback submitted successfully!'); closeFeedback(); })
      .catch(() => { setFeedbackSubmitting(false); showError('Unknown error while submitting feedback.'); });
  }, [feedbackRowId, feedbackText, closeFeedback]);

  useEffect(() => {
    if (!feedbackRowId) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackRowId, closeFeedback]);

  /* ════════════ RENDER ════════════ */
  return (
    <>
      <motion.div
        className="qp-history"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {/* Header */}
        <div className="qp-history__header">
          <h3 className="qp-history__title" style={{ margin: 0 }}>NAICS Classification History</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="cs-client-search__input-wrap" style={{ minWidth: '220px' }}>
              <FiSearch size={13} className="cs-client-search__icon" />
              <input
                className="cs-client-search__input"
                type="text"
                placeholder="Search…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <motion.button
              className="csh-refresh-btn"
              onClick={() => fetchData(page)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <FiRefreshCw size={13} className={loading && rows.length > 0 ? 'csh-spin' : ''} />
              Refresh
            </motion.button>
          </div>
        </div>

        {/* Body */}
        <div className="qp-history__body">
          {error ? (
            <div className="qp-empty-state" style={{ color: 'var(--accent-red)' }}>
              <FiClock size={28} /><p>{error}</p>
            </div>
          ) : loading ? (
            <div className="qp-history-loader" style={{ minHeight: 280 }}>
              <div className="qp-history-loader__orb">
                <div className="qp-history-loader__ring qp-history-loader__ring--1" />
                <div className="qp-history-loader__ring qp-history-loader__ring--2" />
                <div className="qp-history-loader__core"><FiClock size={16} /></div>
              </div>
              <div className="qp-history-loader__text">
                <p className="qp-history-loader__title">Loading History</p>
                <p className="qp-history-loader__sub">
                  Fetching NAICS classification records
                  <span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span>
                </p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="qp-empty-state">
              <FiClock size={28} />
              <p>{search ? 'No records match your search.' : 'No NAICS classifications yet.'}</p>
            </div>
          ) : (
            <div className="qp-table-wrap glass-surface">
              <table className="qp-table">
                <thead>
                  <tr>
                    <th className="qp-table__th">Insured Name</th>
                    <th className="qp-table__th">Searched Text</th>
                    <th className="qp-table__th">Submitted Date</th>
                    <th className="qp-table__th">User</th>
                    <th className="qp-table__th">Status</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Action</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row, i) => {
                    const isSuccess = row.status === 'Success';
                    return (
                      <tr key={row.id ?? i} className="qp-table__row">
                        <td className="qp-table__td">{row.insuredName || '—'}</td>
                        <td className="qp-table__td">{row.searchText}</td>
                        <td className="qp-table__td" style={{ whiteSpace: 'nowrap' }}>{formatDate(row.created)}</td>
                        <td className="qp-table__td">{row.userName}</td>
                        <td className="qp-table__td">
                          <span className="qp-status-pill" style={{ color: (STATUS_CONFIG[row.status] || STATUS_CONFIG.Failed).color }}>
                            {(STATUS_CONFIG[row.status] || { label: row.status }).label}
                          </span>
                        </td>
                        <td className="qp-table__td" style={{ textAlign: 'center' }}>
                          <div className="qp-table__actions">
                            <button
                              className="qp-table__action-btn"
                              title="Preview"
                              onClick={() => onPreviewClick(row)}
                            >
                              <img src={PreviewIcon} alt="Preview" width={30} height={30} />
                            </button>
                          </div>
                        </td>
                        <td className="qp-table__td" style={{ textAlign: 'center' }}>
                          <button
                            className="qp-table__action-btn"
                            title="Feedback"
                            onClick={e => openFeedback(row, e)}
                            style={{ margin: '0 auto' }}
                          >
                            <FiMessageSquare size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="qp-pagination">
              <span className="qp-pagination__info">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
              </span>
              <div className="qp-pagination__controls">
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(1)} title="First">«</button>
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} title="Previous">‹</button>
                {pageList.map((p, i) =>
                  p === '…'
                    ? <span key={`e-${i}`} className="qp-pagination__ellipsis">…</span>
                    : <button
                        key={p}
                        className={`qp-pagination__btn${page === p ? ' qp-pagination__btn--active' : ''}`}
                        onClick={() => setPage(p as number)}
                      >{p}</button>
                )}
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} title="Next">›</button>
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Last">»</button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Feedback popup — portalled to body so it isn't clipped */}
      {feedbackRowId && feedbackAnchor && ReactDOM.createPortal(
        <div
          ref={feedbackRef}
          className="csfb-popup"
          style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}
        >
          <h4 className="csfb-title">Feedback</h4>
          <textarea
            className="csfb-textarea"
            placeholder="Enter your feedback here"
            value={feedbackText}
            onChange={e => setFeedbackText(e.target.value)}
            rows={4}
          />
          {feedBackError && (
            <span style={{ fontSize: '11px', color: 'var(--accent-red, #ef4444)', marginTop: '4px', display: 'block' }}>
              {feedBackError}
            </span>
          )}
          <div className="csfb-rating">
            <button
              className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`}
              onClick={onThumbsUpClick}
              title="Thumbs up"
            >
              <FiThumbsUp size={20} />
            </button>
            <button
              className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`}
              onClick={onThumbsDownClick}
              title="Thumbs down"
            >
              <FiThumbsDown size={20} />
            </button>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
            <button
              className="cs-browse-btn cs-browse-btn--sm"
              onClick={onSubmitFeedback}
              disabled={feedbackSubmitting}
              style={{ flex: 1 }}
            >
              {feedbackSubmitting ? 'Submitting…' : 'Submit'}
            </button>
            <button
              onClick={closeFeedback}
              style={{
                flex: 1, padding: '7px 16px', fontSize: '12px', fontWeight: 600,
                background: 'var(--surface-glass)', color: 'var(--text-secondary)',
                border: '1px solid var(--border-glass)', borderRadius: '8px', cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>,
        document.body,
      )}

      {/* Preview modal — portalled to body */}
      {previewData && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setPreviewData(null)}
        >
          <div
            style={{
              background: '#18181f', borderRadius: '16px',
              border: '1px solid rgba(255,255,255,0.09)',
              padding: '24px', width: '80vw', maxWidth: '1000px', maxHeight: '80vh',
              overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: '16px', paddingBottom: '14px',
              borderBottom: '1px solid rgba(255,255,255,0.07)',
            }}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#ffffff' }}>
                Extracted Data
              </h3>
              <button
                onClick={() => setPreviewData(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', display: 'flex' }}
              >
                <FiX size={18} />
              </button>
            </div>
            <div className="cspr-table-wrap">
              <table className="cspr-table">
                <thead>
                  <tr>
                    <th className="cspr-th">Searched Text</th>
                    <th className="cspr-th">NAICS Code</th>
                    <th className="cspr-th">Description</th>
                    <th className="cspr-th">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((item, i) => (
                    <tr key={i} className="cspr-tr">
                      <td className="cspr-td">{item.SearchedText}</td>
                      <td className="cspr-td">
                        {item.NaicsCode && (
                          <span style={{
                            display: 'inline-block', padding: '2px 10px', borderRadius: '20px',
                            fontSize: '11px', fontWeight: 600,
                            background: 'rgba(139,92,246,0.18)', color: '#c084fc',
                          }}>
                            {item.NaicsCode}
                          </span>
                        )}
                      </td>
                      <td className="cspr-td">{item.Description}</td>
                      <td className="cspr-td">
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                          background: item.Success ? 'rgba(52,211,153,0.15)' : 'rgba(239,68,68,0.15)',
                          color:      item.Success ? '#34d399'               : '#f87171',
                        }}>
                          {item.Success ? '✓ Success' : '✗ Failed'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default NaicsClassificationHistory;

